import React, { useMemo } from 'react';
import ButtonGroup from '@atlaskit/button/button-group';
import LoadingButton from '@atlaskit/button/loading-button';
import Button from '@atlaskit/button/standard-button';
import Form, { ErrorMessage, Field, FormFooter, FormSection, HelperMessage } from '@atlaskit/form';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { RadioGroup } from '@atlaskit/radio';
import SectionMessage from '@atlaskit/section-message';
import Select, { type OptionType, type ValueType } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import TextArea from '@atlaskit/textarea';
import TextField from '@atlaskit/textfield';
import Toggle from '@atlaskit/toggle';
import { useIntl } from '@atlassian/jira-intl';
import type { ConfigType } from '../../../common/types';
import { BACKGROUND_PATTERN_OPTIONS, LIMITS } from './constants';
import messages from './messages';
import type { SettingsFormProps } from './types';
import {
	getRequestOption,
	hexColorValidator,
	requiredWithMaxLengthValidator,
	transformHexValue,
	useWidgetSettingsForm,
} from './utils';

export const SettingsForm = ({
	config,
	onChange,
	projectKey,
	onSubmit,
	submitting,
	isEnableDisabled,
	isRequestTypeValidationLoading,
}: SettingsFormProps) => {
	const { formatMessage } = useIntl();
	const [
		configState,
		initialConfig,
		validateSpy,
		requestTypesState,
		fetchRequestTypes,
		onFormSubmit,
		isFormDirty,
	] = useWidgetSettingsForm({
		config,
		onChange,
		formatMessage,
		projectKey,
		onSubmit,
		isEnableDisabled,
	});

	const requestOptions = useMemo(() => {
		if (!requestTypesState.data) return [];

		return requestTypesState.data?.map((requestType) => ({
			label: requestType.name,
			value: requestType.id,
		}));
	}, [requestTypesState.data]);

	// only show this error message if fetching request type fails for the first time
	if (requestTypesState.error && !requestTypesState.data) {
		return (
			<SectionMessage appearance="error">
				{formatMessage(messages.errorFetchingRequestTypes)}
			</SectionMessage>
		);
	}

	if (!requestTypesState.data || !configState.requestTypeId) {
		return (
			<Box xcss={loadingContainerStyles}>
				<Spinner
					size="large"
					testId="servicedesk-widget-settings.ui.content.settings-form.spinner"
				/>
			</Box>
		);
	}
	const isSaveDisabled =
		requestTypesState.loading ||
		!!requestTypesState.error ||
		(initialConfig.requestTypeId !== null && !isFormDirty) ||
		isEnableDisabled;

	return (
		<Form<ConfigType> onSubmit={onFormSubmit}>
			{({ formProps, reset }) => (
				<form {...formProps} aria-label={formatMessage(messages.formAriaLabel)}>
					<FormSection>
						<Field<boolean>
							name="widgetEnabled"
							label={formatMessage(messages.enableWidgetToggleLabel)}
							validate={validateSpy('widgetEnabled')}
							defaultValue={isEnableDisabled ? false : initialConfig.widgetEnabled}
							isDisabled={isEnableDisabled}
						>
							{({ fieldProps: { value, ...rest } }) => (
								<>
									<br />
									<Toggle isChecked={value} {...rest} />
								</>
							)}
						</Field>

						<Field<ValueType<OptionType | number>>
							name="requestTypeId"
							label={formatMessage(messages.defaultRequestTypeSelectLabel)}
							defaultValue={getRequestOption(
								requestTypesState.data,
								initialConfig.requestTypeId || configState.requestTypeId,
							)}
							validate={validateSpy('requestTypeId')}
						>
							{({ fieldProps, error }) => (
								<>
									<Select
										options={requestOptions}
										{...fieldProps}
										isLoading={requestTypesState.loading || isRequestTypeValidationLoading}
										isSearchable
										onInputChange={fetchRequestTypes}
										filterOption={() => true} // removing default filtering as we filter from api call
									/>
									<HelperMessage>
										{formatMessage(messages.defaultRequestTypeSelectHelpText)}
									</HelperMessage>
									{error && <ErrorMessage>{error}</ErrorMessage>}
									{requestTypesState.error && (
										<ErrorMessage>{formatMessage(messages.errorFetchingRequestTypes)}</ErrorMessage>
									)}
								</>
							)}
						</Field>
						<Field
							name="rtSearchEnabled"
							label={formatMessage(messages.allowSearchForAllRequestTypesToggleLabel)}
							validate={validateSpy('rtSearchEnabled')}
							defaultValue={initialConfig.rtSearchEnabled}
						>
							{({ fieldProps: { value, ...rest } }) => (
								<>
									<br />
									<Toggle isChecked={value} {...rest} />
								</>
							)}
						</Field>
						<Field
							name="kbEnabled"
							label={formatMessage(messages.allowSearchForKnowledgeBaseArticlesToggleLabel)}
							validate={validateSpy('kbEnabled')}
							defaultValue={initialConfig.kbEnabled}
						>
							{({ fieldProps: { value, ...rest } }) => (
								<>
									<br />
									<Toggle isChecked={value} {...rest} />
									<HelperMessage>
										{formatMessage(messages.allowSearchForKnowledgeBaseArticlesToggleHelpText)}
									</HelperMessage>
								</>
							)}
						</Field>
						<Field
							name="primaryColorHex"
							label={formatMessage(messages.colorInputLabel)}
							defaultValue={initialConfig.primaryColorHex}
							validate={validateSpy('primaryColorHex', hexColorValidator)}
							transform={transformHexValue}
							isRequired
						>
							{({ fieldProps, error }) => (
								<>
									<Inline alignBlock="center" space="space.100">
										<TextField {...fieldProps} maxLength={7} />
										<Box
											xcss={colorPreviewStyles}
											// eslint-disable-next-line jira/react/no-style-attribute
											style={{
												backgroundColor: fieldProps.value,
											}}
										/>
									</Inline>

									{error && <ErrorMessage>{error}</ErrorMessage>}
								</>
							)}
						</Field>
						<Field<ValueType<OptionType | string>>
							name="backgroundPattern"
							label={formatMessage(messages.widgetBackgroundPatternSelectLabel)}
							defaultValue={BACKGROUND_PATTERN_OPTIONS.find(
								(v) => v.value === initialConfig.backgroundPattern,
							)}
							validate={validateSpy('backgroundPattern')}
						>
							{({ fieldProps }) => (
								<>
									<Select options={BACKGROUND_PATTERN_OPTIONS} {...fieldProps} />
								</>
							)}
						</Field>
						<Field
							name="widgetButtonTextEnabled"
							label={formatMessage(messages.widgetButtonTypeRadioGroupLabel)}
							validate={validateSpy('widgetButtonTextEnabled')}
							defaultValue={initialConfig.widgetButtonTextEnabled ? 'text' : 'icon'}
						>
							{({ fieldProps }) => (
								<RadioGroup
									options={[
										{
											name: 'icon',
											value: 'icon',
											label: formatMessage(messages.widgetButtonQuestionMarkIconRadioLabel),
										},
										{
											name: 'text',
											value: 'text',
											label: formatMessage(messages.widgetButtonCustomTextRadioLabel),
										},
									]}
									{...fieldProps}
								/>
							)}
						</Field>

						<Field
							name="widgetButtonText"
							label={formatMessage(messages.widgetButtonCustomTextInputLabel)}
							defaultValue={initialConfig.widgetButtonText}
							isDisabled={!configState.widgetButtonTextEnabled}
							validate={validateSpy(
								'widgetButtonText',
								requiredWithMaxLengthValidator(
									formatMessage(messages.widgetButtonCustomTextInputLabel),
									LIMITS.WIDGET_BUTTON_CUSTOM_TEXT_MAX_LENGTH,
								),
							)}
							isRequired
						>
							{({ fieldProps, error }) => (
								<>
									<TextField {...fieldProps} />
									{error && <ErrorMessage>{error}</ErrorMessage>}
								</>
							)}
						</Field>

						<Field
							name="headerText"
							label={formatMessage(messages.widgetHeaderInputLabel)}
							defaultValue={initialConfig.headerText}
							validate={validateSpy(
								'headerText',
								requiredWithMaxLengthValidator(
									formatMessage(messages.widgetHeaderInputLabel),
									LIMITS.HEADER_TEXT_MAX_LENGTH,
								),
							)}
							isRequired
						>
							{({ fieldProps, error }) => (
								<>
									<TextField {...fieldProps} />
									{error && <ErrorMessage>{error}</ErrorMessage>}
								</>
							)}
						</Field>
						<Field<string, HTMLTextAreaElement>
							name="confirmationText"
							label={formatMessage(messages.confirmationMessageInputLabel)}
							defaultValue={initialConfig.confirmationText}
							validate={validateSpy(
								'confirmationText',
								requiredWithMaxLengthValidator(
									formatMessage(messages.confirmationMessageInputLabel),
									LIMITS.CONFIRMATION_TEXT_MAX_LENGTH,
								),
							)}
							isRequired
						>
							{({ fieldProps, error }) => (
								<>
									<TextArea {...fieldProps} />
									{error && <ErrorMessage>{error}</ErrorMessage>}
								</>
							)}
						</Field>
					</FormSection>

					<FormFooter align="start">
						<ButtonGroup>
							<LoadingButton
								type="submit"
								appearance="primary"
								isLoading={submitting}
								isDisabled={isSaveDisabled}
							>
								{formatMessage(messages.saveButtonLabel)}
							</LoadingButton>

							<Button
								appearance="subtle"
								onClick={() => {
									reset();
								}}
								isDisabled={!isFormDirty}
							>
								{formatMessage(messages.cancelButtonLabel)}
							</Button>
						</ButtonGroup>
					</FormFooter>
				</form>
			)}
		</Form>
	);
};

export default SettingsForm;

const colorPreviewStyles = xcss({
	borderRadius: 'border.radius.circle',
	minHeight: 'size.300',
	minWidth: 'size.300',
	borderWidth: 'border.width.outline',
	borderColor: 'color.border',
	borderStyle: 'solid',
});

const loadingContainerStyles = xcss({
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	minHeight: 'size.1000',
});
